import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAlunosEmAtencao } from '../../api/hooks/useObservacoes';
import type { ClassificacaoRisco } from '../../api/hooks/useObservacoes';

const RISCO_CONFIG: Record<ClassificacaoRisco, { label: string; color: string; bg: string }> = {
  BEM:          { label: 'Bem', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  ATENCAO:      { label: 'Atenção', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  PREOCUPANTE:  { label: 'Preocupante', color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
  CRITICO:      { label: 'Crítico', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
};

interface AlunosEmAtencaoCardProps {
  professorId: string;
  onClickAluno?: (alunoId: string) => void;
}

/**
 * Card de "Alunos em Atenção" para o dashboard do professor (Fase 4).
 * Mostra os 5 alunos com maior score de risco nas turmas do professor.
 */
export function AlunosEmAtencaoCard({ professorId, onClickAluno }: AlunosEmAtencaoCardProps) {
  const { data: alunos = [], isLoading, isError, refetch, isFetching } = useAlunosEmAtencao(professorId);
  const [showAll, setShowAll] = React.useState(false);

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
          <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>Alunos em Atenção</span>
        </div>
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Carregando...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <AlertTriangle size={16} style={{ color: '#ef4444' }} />
          <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>Alunos em Atenção</span>
        </div>
        <div style={{ textAlign: 'center', padding: '16px 14px 18px', color: 'var(--text-muted, #999)', fontSize: '0.82rem' }}>
          Não foi possível carregar a lista.
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            style={{
              display: 'block', margin: '10px auto 0', padding: '6px 12px',
              border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8,
              background: 'var(--bg-main, #f9fafb)', color: '#6366f1',
              fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {isFetching ? 'Tentando…' : 'Tentar de novo'}
          </button>
        </div>
      </div>
    );
  }

  if (alunos.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <AlertTriangle size={16} style={{ color: '#22c55e' }} />
          <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>Alunos em Atenção</span>
        </div>
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted, #999)', fontSize: '0.82rem' }}>
          Nenhum aluno com risco elevado <CheckCircle2 size={14} style={{ display: 'inline', verticalAlign: 'middle', color: '#22c55e' }} />
        </div>
      </div>
    );
  }

  const LIMIT = 3;
  const alunosVisiveis = showAll ? alunos : alunos.slice(0, LIMIT);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
        <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>Alunos em Atenção</span>
        <span style={{
          marginLeft: 'auto', fontSize: '0.65rem', padding: '2px 8px',
          borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: 700,
        }}>
          {alunos.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 14px 14px' }}>
        {alunosVisiveis.map(a => {
          const risco = RISCO_CONFIG[a.classificacao] || RISCO_CONFIG.ATENCAO;
          return (
            <div
              key={a.alunoId}
              onClick={() => onClickAluno?.(a.alunoId)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 10, background: 'var(--bg-main, #f9fafb)',
                cursor: onClickAluno ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: risco.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 800, color: risco.color,
              }}>
                {a.score}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-main)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
                }}>
                  {a.nome}
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                  <span>{a.turma}</span>
                  <span>Freq: {a.frequencia.toFixed(0)}%</span>
                  <span>Média: {a.mediaGeral.toFixed(1)}</span>
                </div>
              </div>
              <span style={{
                padding: '2px 8px', borderRadius: 6, fontSize: '0.6rem',
                fontWeight: 700, background: risco.bg, color: risco.color,
                textTransform: 'uppercase' as const,
              }}>
                {risco.label}
              </span>
            </div>
          );
        })}
        {alunos.length > LIMIT && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            style={{
              padding: '8px 0', border: 'none', background: 'transparent',
              color: '#6366f1', fontSize: '0.78rem', fontWeight: 600,
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            Ver todos ({alunos.length} alunos)
          </button>
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  borderRadius: 10, background: 'var(--bg-card, #fff)',
  border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '10px 14px 8px',
};
